/**
 * EYVO Command Center — Military-grade tactical display.
 * Real-time document tracking with animated transitions.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Maximize2, Minimize2, RefreshCw, Settings, X, ChevronLeft, Home,
  ClipboardList, FileQuestion, ShoppingCart, Receipt, Radio, Zap, BarChart3
} from 'lucide-react';

const STATUS_COLOR = {
  DRAFT: '#64748b', DORMANT: '#475569',
  PENDING: '#f59e0b', 'PENDING APPROVAL': '#f59e0b',
  UNAPPROVED: '#f97316',
  APPROVED: '#22c55e', ISSUED: '#10b981',
  PRINTED: '#14b8a6', RECEIVED: '#0d9488',
  COMPLETED: '#16a34a', CLOSED: '#64748b',
  CANCELLED: '#ef4444', REJECTED: '#dc2626',
  PAID: '#059669', OVERDUE: '#e11d48',
  ORDER: '#3b82f6', RFQ: '#8b5cf6',
};

const LANE_CONFIG = {
  requisitions:    { icon: ClipboardList, label: 'REQUISITIONS',     accent: '#3b82f6', glow: 'rgba(59,130,246,0.3)' },
  rfqs:            { icon: FileQuestion,  label: 'RFx SOURCING',      accent: '#8b5cf6', glow: 'rgba(139,92,246,0.3)' },
  purchase_orders: { icon: ShoppingCart,  label: 'PURCHASE ORDERS',  accent: '#10b981', glow: 'rgba(16,185,129,0.3)' },
  invoices:        { icon: Receipt,       label: 'INVOICES',         accent: '#f59e0b', glow: 'rgba(245,158,11,0.3)' },
};

const fmtCur = (v, sym) => `${sym}${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (d, tz = 'America/Los_Angeles') => { if (!d) return ''; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: tz }); } catch { return ''; } };
const fmtDateTime = (d, tz = 'America/Los_Angeles') => { if (!d) return ''; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }); } catch { return ''; } };
const agedDays = (d) => { if (!d) return null; try { return Math.floor((Date.now() - new Date(d).getTime()) / 86400000); } catch { return null; } };

function TacticalCard({ doc, laneKey, currency, textSize, isNew, isTransition }) {
  const [hover, setHover] = useState(false);
  const color = STATUS_COLOR[doc.status?.toUpperCase()] || '#64748b';
  const sym = currency === 'GBP' ? '\u00a3' : currency === 'EUR' ? '\u20ac' : '$';
  const ageDays = agedDays(doc.date);
  const lastUpdateDays = agedDays(doc.updated_at);
  const ageColor = ageDays > 30 ? '#ef4444' : ageDays > 14 ? '#f59e0b' : '#22c55e';
  const sz = [
    { id: 'text-[8px]', desc: 'text-[7px]', mono: 'text-[8px]' },
    { id: 'text-[10px]', desc: 'text-[9px]', mono: 'text-[9px]' },
    { id: 'text-xs', desc: 'text-[11px]', mono: 'text-[11px]' },
    { id: 'text-sm', desc: 'text-xs', mono: 'text-xs' },
  ][textSize || 1];

  return (
    <div
      className={`relative border rounded mb-1 transition-all duration-500 ${isNew ? 'animate-flash' : ''} ${isTransition ? 'animate-slide-in' : ''}`}
      style={{
        borderColor: `${color}40`,
        background: `linear-gradient(135deg, ${color}10, transparent)`,
      }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
    >
      {/* Status glow bar */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}80` }} />

      <div className="pl-3 pr-2 py-1.5">
        <div className="flex items-center gap-2">
          <span className={`font-mono ${sz.mono} font-bold`} style={{ color }}>{doc.id}</span>
          {doc.event_type && <span className={`${sz.desc} uppercase tracking-widest font-bold px-1 py-0.5 rounded`}
            style={{ color: '#8b5cf6', backgroundColor: 'rgba(139,92,246,0.1)', fontSize: '7px' }}>{doc.event_type === 'REVERSE_AUCTION' ? 'AUCTION' : doc.event_type}</span>}
          <span className={`${sz.desc} text-slate-400 truncate flex-1`}>{doc.description || '\u2014'}</span>
          {doc.value > 0 && <span className={`font-mono ${sz.mono} font-bold text-slate-200`}>{fmtCur(doc.value, sym)}</span>}
          {doc.supplier_count > 0 && <span className={`${sz.desc} text-slate-500`}>{doc.supplier_count} suppliers</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`${sz.desc} uppercase tracking-widest font-bold`} style={{ color, fontSize: '7px' }}>{doc.status}</span>
          {doc.approval_progress && (
            <span className={`font-mono ${sz.desc} px-1 py-0.5 rounded`}
              style={{ color: '#38bdf8', backgroundColor: 'rgba(56,189,248,0.1)', fontSize: '7px' }}>
              {doc.approval_progress} approvals
            </span>
          )}
          {doc.owner && <span className={`${sz.desc} text-slate-500`}>{doc.owner}</span>}
          <span className="flex items-center gap-1 ml-auto">
            {ageDays !== null && <span className={`font-mono ${sz.desc} font-bold`} style={{ color: ageColor }} title={`Created ${ageDays}d ago`}>{ageDays}d</span>}
            {lastUpdateDays !== null && lastUpdateDays !== ageDays && <span className={`font-mono ${sz.desc} text-slate-600`} title={`Updated ${lastUpdateDays}d ago`}>/{lastUpdateDays}d</span>}
          </span>
        </div>
      </div>

      {/* Hover detail overlay */}
      {hover && (
        <div className="absolute z-50 left-full ml-2 top-0 w-72 border rounded-lg shadow-2xl p-3 text-[10px] pointer-events-none"
          style={{ background: '#0a0e1a', borderColor: `${color}60`, boxShadow: `0 0 20px ${color}20` }}>
          <div className="font-mono font-bold text-xs mb-2" style={{ color }}>{doc.id}</div>
          <div className="grid grid-cols-2 gap-1 text-slate-400">
            {doc.description && <div className="col-span-2"><span className="text-slate-600">DESC:</span> <span className="text-slate-300">{doc.description}</span></div>}
            <div><span className="text-slate-600">STATUS:</span> <span style={{ color }} className="font-bold">{doc.status}</span></div>
            <div><span className="text-slate-600">VALUE:</span> <span className="text-slate-200 font-bold">{sym}{Number(doc.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
            {doc.owner && <div><span className="text-slate-600">ORIGIN:</span> <span className="text-slate-300">{doc.owner}</span></div>}
            {doc.supplier && <div><span className="text-slate-600">SUPPLIER:</span> <span className="text-slate-300">{doc.supplier}</span></div>}
            {doc.date && <div><span className="text-slate-600">DATE:</span> <span className="text-slate-300">{fmtDateTime(doc.date)}</span></div>}
            {ageDays !== null && <div><span className="text-slate-600">AGE:</span> <span className="font-bold" style={{ color: ageColor }}>{ageDays} days</span></div>}
            {lastUpdateDays !== null && <div><span className="text-slate-600">LAST UPD:</span> <span className="text-slate-300">{lastUpdateDays} days ago</span></div>}
            {doc.ref_num && <div><span className="text-slate-600">REF:</span> <span className="text-slate-300">{doc.ref_num}</span></div>}
            {doc.approval_progress && <div><span className="text-slate-600">APPROVALS:</span> <span className="text-cyan-300 font-bold">{doc.approval_progress}</span></div>}
            {doc.line_count > 0 && <div><span className="text-slate-600">ITEMS:</span> <span className="text-slate-300">{doc.line_count}</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}

function TacticalLane({ laneKey, documents, isFocused, onFocus, currency, textSize, newIds, transitionIds }) {
  const config = LANE_CONFIG[laneKey];
  const Icon = config.icon;
  const sym = currency === 'GBP' ? '\u00a3' : currency === 'EUR' ? '\u20ac' : '$';
  const total = documents.reduce((s, d) => s + (Number(d.value) || 0), 0);

  // Sort documents by document number descending (highest first)
  const sortedDocs = [...documents].sort((a, b) => (b.id || '').localeCompare(a.id || '', undefined, { numeric: true }));

  return (
    <div className={`flex flex-col ${isFocused ? 'flex-1' : 'flex-1 min-w-[220px]'}`}>
      {/* Lane header */}
      <button onClick={() => onFocus(laneKey)}
        className="relative overflow-hidden px-3 py-2.5 rounded-t-lg flex items-center gap-2 transition-all hover:brightness-125"
        style={{ background: `linear-gradient(135deg, ${config.accent}30, ${config.accent}10)`, borderBottom: `2px solid ${config.accent}` }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.03) 3px, rgba(255,255,255,0.03) 4px)' }} />
        <Icon className="h-4 w-4" style={{ color: config.accent }} />
        <span className="font-mono font-bold text-[11px] tracking-widest" style={{ color: config.accent }}>{config.label}</span>
        <span className="font-mono text-[9px] text-slate-400 ml-1">{sym}{total.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: config.accent, boxShadow: `0 0 6px ${config.accent}` }} />
          <span className="font-mono text-[10px] font-bold" style={{ color: config.accent }}>{documents.length}</span>
        </div>
      </button>

      {/* Lane body */}
      <div className="flex-1 rounded-b-lg overflow-y-auto p-1.5"
        style={{
          background: 'linear-gradient(180deg, rgba(10,14,26,0.95), rgba(5,8,18,0.98))',
          border: `1px solid ${config.accent}15`,
          borderTop: 'none',
          maxHeight: isFocused ? 'calc(100vh - 120px)' : 'calc(100vh - 130px)',
        }}>
        {documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-700">
            <Radio className="h-6 w-6 mb-2 opacity-30" />
            <span className="font-mono text-[9px] tracking-widest">NO SIGNALS</span>
          </div>
        ) : (
          sortedDocs.map(doc => (
            <TacticalCard key={doc.id} doc={doc} laneKey={laneKey} currency={currency} textSize={textSize}
              isNew={newIds.has(doc.id)} isTransition={transitionIds.has(doc.id)} />
          ))
        )}
      </div>
    </div>
  );
}

function StatBlock({ label, value, sub, color }) {
  return (
    <div className="rounded-lg px-3 py-2.5" style={{ background: `${color}08`, border: `1px solid ${color}25` }} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <p className="font-mono text-[8px] font-bold tracking-[0.15em] uppercase" style={{ color: `${color}90` }}>{label}</p>
      <p className="font-mono text-lg font-black mt-0.5" style={{ color, textShadow: `0 0 12px ${color}40` }}>{value}</p>
      <p className="font-mono text-[9px] mt-0.5" style={{ color: '#64748b' }}>{sub}</p>
    </div>
  );
}


function StatusLegend() {
  const items = [
    { s: 'DRAFT', c: '#64748b' }, { s: 'PENDING', c: '#f59e0b' },
    { s: 'APPROVED', c: '#22c55e' }, { s: 'ISSUED', c: '#10b981' },
    { s: 'CANCELLED', c: '#ef4444' }, { s: 'COMPLETED', c: '#16a34a' },
  ];
  return (
    <div className="flex items-center gap-2">
      {items.map(i => (
        <span key={i.s} className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: i.c, boxShadow: `0 0 4px ${i.c}80` }} />
          <span className="font-mono text-[7px] text-slate-500 uppercase tracking-wider">{i.s}</span>
        </span>
      ))}
    </div>
  );
}

export default function ATCPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ requisitions: [], rfqs: [], purchase_orders: [], invoices: [], currency: 'USD' });
  const [prefs, setPrefs] = useState({ visible_lanes: ['requisitions', 'purchase_orders', 'invoices'], auto_refresh: true, refresh_interval: 10 });
  const [focusedLane, setFocusedLane] = useState(null);
  const [textSize, setTextSize] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [newIds, setNewIds] = useState(new Set());
  const [transitionIds, setTransitionIds] = useState(new Set());
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  const prevDataRef = useRef({});
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/api/atc/documents');
      const newData = r.data;

      // Detect changes for animations
      const prev = prevDataRef.current;
      const arrivals = new Set();
      const transitions = new Set();

      // Build a set of all IDs in previous data per lane
      const prevAllIds = new Set();
      ['requisitions', 'rfqs', 'purchase_orders', 'invoices'].forEach(lane => {
        (prev[lane] || []).forEach(d => prevAllIds.add(d.id));
      });

      // Build a set of previous req IDs and their source_req values for cross-lane transition detection
      const prevReqIds = new Set((prev.requisitions || []).map(d => d.id));
      const prevReqNumbers = new Set((prev.requisitions || []).map(d => d.id)); // Using ID as request number

      // Track requisitions that changed status (e.g., went to ORDER)
      const prevReqStatuses = {};
      (prev.requisitions || []).forEach(d => { prevReqStatuses[d.id] = d.status; });

      ['requisitions', 'rfqs', 'purchase_orders', 'invoices'].forEach(lane => {
        const prevIds = new Set((prev[lane] || []).map(d => d.id));
        (newData[lane] || []).forEach(d => {
          if (!prevIds.has(d.id)) {
            // New in this lane — check if it came from another lane
            if (prevAllIds.has(d.id)) {
              transitions.add(d.id); // same ID moved between lanes
            } else if (lane === 'purchase_orders') {
              // PO created from a requisition - flash the PO
              transitions.add(d.id);
              // Also check if source requisition number exists and flash it
              if (d.source_req && prevReqNumbers.has(d.source_req)) {
                transitions.add(d.source_req);
              }
            } else if (Object.keys(prev).length > 0) {
              arrivals.add(d.id); // brand new document
            }
          } else if (lane === 'requisitions') {
            // Check if requisition status changed to ORDER (meaning a PO was created)
            const prevStatus = prevReqStatuses[d.id];
            if (prevStatus && prevStatus !== d.status && d.status === 'ORDER') {
              transitions.add(d.id); // requisition converted to order
            }
          }
        });
      });

      setNewIds(arrivals);
      setTransitionIds(transitions);
      if (arrivals.size > 0 || transitions.size > 0) {
        // Keep the flash visible for 3 seconds
        setTimeout(() => { setNewIds(new Set()); setTransitionIds(new Set()); }, 3000);
      }

      prevDataRef.current = newData;
      setData(newData);
      setLastUpdate(new Date());
    } catch {}
  }, []);

  useEffect(() => { fetchData(); api.get('/api/atc/user-preferences').then(r => { if (r.data?.visible_lanes) setPrefs(r.data); }).catch(() => {}); }, [fetchData]);
  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (prefs.auto_refresh && !document.hidden) {
        intervalRef.current = setInterval(fetchData, (prefs.refresh_interval || 10) * 1000);
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      } else {
        fetchData();
        startPolling();
      }
    };
    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [prefs.auto_refresh, prefs.refresh_interval, fetchData]);

  const savePrefs = (p) => { setPrefs(p); api.put('/api/atc/user-preferences', p).catch(() => {}); };
  const toggleLane = (l) => { const v = prefs.visible_lanes.includes(l) ? prefs.visible_lanes.filter(x => x !== l) : [...prefs.visible_lanes, l]; savePrefs({ ...prefs, visible_lanes: v }); };
  const handleFocus = (l) => setFocusedLane(focusedLane === l ? null : l);
  const toggleFS = () => { if (!document.fullscreenElement) { document.documentElement.requestFullscreen(); setIsFullscreen(true); } else { document.exitFullscreen(); setIsFullscreen(false); } };
  const toggleStats = () => {
    const next = !showStats;
    setShowStats(next);
    if (next && !stats) {
      api.get('/api/atc/efficiency-stats').then(r => setStats(r.data)).catch(() => {});
    }
  };

  const totalDocs = Object.values(data).flat().filter(d => typeof d === 'object').length;
  const sym = data.currency === 'GBP' ? '\u00a3' : data.currency === 'EUR' ? '\u20ac' : '$';
  const totalVal = Object.values(data).flat().filter(d => typeof d === 'object').reduce((s, d) => s + (Number(d.value) || 0), 0);
  const visibleLanes = focusedLane ? [focusedLane] : prefs.visible_lanes;

  return (
    <div className="min-h-screen select-none" style={{ background: 'radial-gradient(ellipse at center, #0a0e1a 0%, #050810 100%)' }}>
      {/* Scanline overlay */}
      <div className="fixed inset-0 pointer-events-none z-10 opacity-[0.03]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 3px)' }} />

      {/* Grid overlay */}
      <div className="fixed inset-0 pointer-events-none z-10 opacity-[0.02]"
        style={{ backgroundImage: 'linear-gradient(rgba(59,130,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      {/* Header */}
      <div className="relative z-20 px-4 py-2 flex items-center justify-between border-b" style={{ background: 'rgba(10,14,26,0.9)', borderColor: '#1e293b' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-cyan-400 transition-colors" title="Back to Dashboard">
            <Home className="h-5 w-5" />
          </button>
          {focusedLane && <button onClick={() => setFocusedLane(null)} className="text-slate-500 hover:text-white"><ChevronLeft className="h-5 w-5" /></button>}
          <div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-cyan-400" style={{ filter: 'drop-shadow(0 0 4px rgba(34,211,238,0.5))' }} />
              <h1 className="font-mono text-sm font-bold tracking-[0.2em] text-cyan-300" style={{ textShadow: '0 0 10px rgba(34,211,238,0.3)' }}>
                COMMAND CENTER
              </h1>
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" style={{ boxShadow: '0 0 8px rgba(74,222,128,0.6)' }} />
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="font-mono text-[10px] text-slate-400">
                {totalDocs} ACTIVE · {sym}{totalVal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                {lastUpdate && <span className="text-slate-600"> · {lastUpdate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })}</span>}
              </span>
              <StatusLegend />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!focusedLane && Object.entries(LANE_CONFIG).map(([k, cfg]) => {
            const I = cfg.icon; const active = prefs.visible_lanes.includes(k);
            return (
              <button key={k} onClick={() => toggleLane(k)}
                className="flex items-center gap-1 px-2 py-1 rounded font-mono text-[9px] tracking-wider font-bold transition-all"
                style={active
                  ? { background: `${cfg.accent}20`, color: cfg.accent, border: `1px solid ${cfg.accent}40` }
                  : { background: '#0f172a', color: '#475569', border: '1px solid #1e293b' }
                }>
                <I className="h-3 w-3" />{cfg.label.split(' ')[0]}
                <span className="opacity-60">{data[k]?.length || 0}</span>
              </button>
            );
          })}
          <div className="w-px h-5 bg-slate-800 mx-1" />
          <div className="flex items-center rounded overflow-hidden border border-slate-800">
            {['A\u207b', 'A', 'A\u207a', 'A\u207a\u207a'].map((label, i) => (
              <button key={i} onClick={() => setTextSize(i)}
                className={`px-1.5 py-0.5 font-mono text-[9px] font-bold transition-all ${textSize === i ? 'bg-cyan-900/50 text-cyan-300' : 'text-slate-600 hover:text-slate-400'}`}>
                {label}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-cyan-400" onClick={fetchData}><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className={`h-7 w-7 ${showStats ? 'text-cyan-400' : 'text-slate-500 hover:text-cyan-400'}`} onClick={toggleStats} title="Efficiency Stats"><BarChart3 className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-cyan-400" onClick={() => setShowSettings(!showSettings)}><Settings className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-cyan-400" onClick={toggleFS}>{isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}</Button>
        </div>
      </div>

      {showSettings && (
        <div className="relative z-20 px-4 py-2 flex items-center gap-4 border-b text-[10px]" style={{ background: 'rgba(10,14,26,0.8)', borderColor: '#1e293b' }}>
          <span className="font-mono text-slate-500 tracking-wider">AUTO REFRESH</span>
          <Switch checked={prefs.auto_refresh} onCheckedChange={v => savePrefs({ ...prefs, auto_refresh: v })} />
          <select value={prefs.refresh_interval} onChange={e => savePrefs({ ...prefs, refresh_interval: parseInt(e.target.value) })}
            className="h-6 w-14 rounded bg-slate-900 border-slate-700 text-[10px] text-cyan-300 px-1 font-mono">
            {[5,10,15,30,60].map(s => <option key={s} value={s}>{s}s</option>)}
          </select>
          <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-600" onClick={() => setShowSettings(false)}><X className="h-3 w-3" /></Button>
        </div>
      )}

      {/* Efficiency Stats Panel */}
      {showStats && (
        <div className="relative z-20 px-4 py-3 border-b" style={{ background: 'rgba(10,14,26,0.95)', borderColor: '#1e293b' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-cyan-400" />
              <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-cyan-300">EFFICIENCY METRICS</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setStats(null); api.get('/api/atc/efficiency-stats').then(r => setStats(r.data)).catch(() => {}); }}
                className="font-mono text-[9px] text-slate-500 hover:text-cyan-400 transition-colors tracking-wider">REFRESH</button>
              <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-600" onClick={() => setShowStats(false)}><X className="h-3 w-3" /></Button>
            </div>
          </div>
          {!stats ? (
            <div className="text-center py-4">
              <span className="font-mono text-[10px] text-slate-500 tracking-wider animate-pulse">COMPUTING METRICS...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="efficiency-stats-grid">
              <StatBlock label="REQ → PO" value={stats.avg_req_to_po_days != null ? `${stats.avg_req_to_po_days}d` : '—'} sub={`${stats.req_to_po_count || 0} orders`} color="#3b82f6" />
              <StatBlock label="PO → RECEIPT" value={stats.avg_po_to_receipt_days != null ? `${stats.avg_po_to_receipt_days}d` : '—'} sub={`${stats.po_to_receipt_count || 0} completed`} color="#10b981" />
              <StatBlock label="INV → PAYMENT" value={stats.avg_invoice_to_payment_days != null ? `${stats.avg_invoice_to_payment_days}d` : '—'} sub={`${stats.invoice_to_payment_count || 0} paid`} color="#f59e0b" />
              <StatBlock label="AVG PO VALUE" value={`${stats.currency === 'GBP' ? '\u00a3' : stats.currency === 'EUR' ? '\u20ac' : '$'}${(stats.avg_po_value || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}`} sub={`${stats.po_count || 0} orders`} color="#8b5cf6" />
              <StatBlock label="APPROVAL TIME" value={stats.avg_approval_days != null ? `${stats.avg_approval_days}d` : '—'} sub={`${stats.approval_count || 0} approved`} color="#06b6d4" />
              <StatBlock label="TOTAL PO SPEND" value={`${stats.currency === 'GBP' ? '\u00a3' : stats.currency === 'EUR' ? '\u20ac' : '$'}${(stats.total_po_value || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}`} sub={`${stats.min_po_value?.toLocaleString('en-US', {maximumFractionDigits: 0}) || 0} — ${stats.max_po_value?.toLocaleString('en-US', {maximumFractionDigits: 0}) || 0}`} color="#ec4899" />
            </div>
          )}
        </div>
      )}

      {/* Lanes */}
      <div className="relative z-20 flex gap-2 p-2" style={{ height: 'calc(100vh - 60px)' }}>
        {visibleLanes.map(lane => (
          <TacticalLane key={lane} laneKey={lane} documents={data[lane] || []}
            isFocused={focusedLane === lane} onFocus={handleFocus}
            currency={data.currency || 'USD'} textSize={textSize}
            newIds={newIds} transitionIds={transitionIds} />
        ))}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes flash-in {
          0% { opacity: 0.3; transform: scale(0.97); background-color: rgba(34,211,238,0.4); box-shadow: 0 0 30px rgba(34,211,238,0.7), inset 0 0 20px rgba(34,211,238,0.3); }
          20% { opacity: 1; background-color: rgba(34,211,238,0.5); box-shadow: 0 0 40px rgba(34,211,238,0.8), inset 0 0 30px rgba(34,211,238,0.4); }
          40% { background-color: rgba(34,211,238,0.2); box-shadow: 0 0 25px rgba(34,211,238,0.5); }
          60% { background-color: rgba(34,211,238,0.4); box-shadow: 0 0 35px rgba(34,211,238,0.6); }
          80% { background-color: rgba(34,211,238,0.15); box-shadow: 0 0 20px rgba(34,211,238,0.3); }
          100% { transform: scale(1); background-color: transparent; box-shadow: none; }
        }
        @keyframes slide-in {
          0% { opacity: 0.3; transform: translateX(-40px); background-color: rgba(16,185,129,0.5); box-shadow: 0 0 35px rgba(16,185,129,0.7), inset 0 0 25px rgba(16,185,129,0.4); }
          30% { opacity: 1; transform: translateX(4px); background-color: rgba(16,185,129,0.4); }
          50% { background-color: rgba(16,185,129,0.2); }
          70% { background-color: rgba(16,185,129,0.35); box-shadow: 0 0 25px rgba(16,185,129,0.5); }
          100% { transform: translateX(0); background-color: transparent; box-shadow: none; }
        }
        .animate-flash { animation: flash-in 2s ease-out; }
        .animate-slide-in { animation: slide-in 2s ease-out; }
      `}</style>
    </div>
  );
}
